import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useToast,
} from "randmarcomps";
import {
  type ManufacturerCategory,
  usePostV4ManufacturerByRouteManufacturerIdCategoryAndCategoryCodeFieldNameMutation,
} from "randmar-api-client";
import { Info, Loader2, Pencil, Percent } from "lucide-react";
import { useUser } from "../../hooks/useUser";

/**
 * The API might return null for margin values. This helper ensures we treat null as 0.
 * @param value The value to check.
 * @returns The value or 0 if null/undefined.
 */
const getValueOrZero = (value: number | null | undefined): number => value ?? 0;

interface NewMarginsGridProps {
  category: ManufacturerCategory;
}

/**
 * Displays a grid of reseller margins for a given category and allows
 * authorized users to edit them.
 */
function NewMarginsGrid({ category }: NewMarginsGridProps) {
  const { isSuperAdmin } = useUser();
  
  const { applicationId } = useParams<{ applicationId: string }>();
  const { toast } = useToast();
  const [updateCategoryField, { isLoading: isUpdating }] =
    usePostV4ManufacturerByRouteManufacturerIdCategoryAndCategoryCodeFieldNameMutation();

  // State to manage the margin currently being edited in the dialog
  const [editingMargin, setEditingMargin] = useState<{
    fieldName: string;
    label: string;
    currentValue: number;
  } | null>(null);

  // State for the new value being entered in the dialog's input
  const [newValue, setNewValue] = useState("");

  /**
   * Opens the edit dialog and populates it with the selected margin's data.
   */
  const handleEditClick = (
    fieldName: string,
    label: string,
    currentValue: number
  ) => {
    setEditingMargin({ fieldName, label, currentValue });
    setNewValue(String(currentValue));
  };

  /**
   * Handles saving the new margin value by calling the API mutation.
   * Provides user feedback via toasts.
   */
  const handleSave = async () => {
    if (!editingMargin || newValue === "" || isNaN(Number(newValue))) {
      toast({
        variant: "destructive",
        title: "Invalid Input",
        description: "Please enter a valid number for the margin.",
      });
      return;
    }
    try {
      await updateCategoryField({
        routeManufacturerId: applicationId!,
        categoryCode: category.CategoryCode!,
        fieldName: editingMargin.fieldName,
        newValue: newValue,
      }).unwrap();

      toast({
        variant: "success",
        title: "Success",
        description: `${editingMargin.label} margin has been updated.`,
      });
      setEditingMargin(null);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Update Failed",
        description: "The new margin could not be saved. Please try again.",
      });
    }
  };

  // Defines the different margin tiers for rendering in the table
  const marginTiers = [
    { label: "Exclusive", fieldName: "A_GP", value: category.A_GP },
    {
      label: "E‑Commerce Academy",
      fieldName: "B_GP",
      value: category.B_GP,
    },
    { label: "Commercial", fieldName: "C_GP", value: category.C_GP },
    { label: "Novexco Basics", fieldName: "D_GP", value: category.D_GP },
    {
      label: "Cost",
      fieldName: "Cost_GP",
      value: category.Cost_GP,
      adminOnly: true,
    },
  ];

  return (
    <>
      <CardHeader>
        <CardTitle>Reseller Margins</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert variant="info">
          <Info className="h-4 w-4" />
          <AlertTitle>Reseller Packages</AlertTitle>
          <AlertDescription>
            These are the gross profit margins (%) for the different reseller
            tiers in this category.
          </AlertDescription>
        </Alert>

        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                {marginTiers.map(
                  (tier) =>
                    (!tier.adminOnly || isSuperAdmin) && (
                      <TableHead key={tier.fieldName} className="text-center">
                        {tier.label}
                      </TableHead>
                    )
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                {marginTiers.map(
                  (tier) =>
                    (!tier.adminOnly || isSuperAdmin) && (
                      <TableCell key={tier.fieldName} className="text-center">
                        <div className="flex items-center justify-center gap-2 h-9">
                          <span>{getValueOrZero(tier.value)}%</span>
                          {isSuperAdmin && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() =>
                                handleEditClick(
                                  tier.fieldName,
                                  tier.label,
                                  getValueOrZero(tier.value)
                                )
                              }
                            >
                              <Pencil className="h-4 w-4 text-muted-foreground" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    )
                )}
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </CardContent>

      {/* Dialog for Editing a Margin */}
      <Dialog
        open={!!editingMargin}
        onOpenChange={(open) => !open && setEditingMargin(null)}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit {editingMargin?.label} Margin</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="margin-value" className="text-right">
                Margin
              </Label>
              <div className="relative col-span-3">
                <Input
                  id="margin-value"
                  type="number"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  className="pr-8"
                  placeholder="e.g. 40"
                />
                <Percent className="absolute right-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditingMargin(null)}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isUpdating}>
              {isUpdating && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default NewMarginsGrid;
